import {
  getActiveSessionMetadata,
  loadSessionMetadata,
} from "lib/session-metadata";
import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { captureDB } from "lib/indexed-db";
import { CaptureType } from "types";
import type { RtmsRun } from "lib/rtms-sequence";

export interface StepDetailsData {
  captureEntryId?: string;
  rtmsSequenceDetails?: {
    sequenceId: string;
    fields: NonNullable<StepDetailsData["stepDetailsFields"]>;
  };
  rtmsRun?: RtmsRun;
  stepNumber: number;
  stepDetails: Array<string>;
  stepDetailsFields?: Array<{
    label_id: string;
    label_title: string;
    label_type: string;
    value: string;
  }>;
}

export interface StepCaptureData {
  captureEntryId?: string;
  sessionMetadata?: Record<string, string>;
  rtmsConfirmation?: {
    fingerprint: string;
    confirmedAt: number;
    runId: string;
    launchId: string;
    attempt: number;
  };
  stepNumber: number;
  stepLabel: string;
  stepTitle: string;
  numberOfCaptures: number;
  captureTaskId?: string;
  captureTaskStepNumber?: number;
  captureType?: CaptureType;
  schemaFingerprint?: string;
  files: File[];
  stepDetails?: Array<string>; // Step-specific details
  isComplete?: boolean; // Whether this step is fully complete (files + required details)
  collectionDetails?: any; // Task-wide collection details (for step 0)
  orbConfirmation?: {
    fingerprint: string;
    confirmedAt: number;
  };
  orbRetryCount?: number;
  uploadCaptureId?: string;
  uploadedFileKeys?: string[];
}

export interface MultiStepCaptureContextInterface {
  stepCaptures: StepCaptureData[];
  stepDetails: StepDetailsData[];
  currentTaskId: string | null;
  isLoadingSteps: boolean;
  setCurrentTaskId: (taskId: string) => void;
  invalidateCurrentTask: () => void;
  addStepCapture: (stepData: StepCaptureData) => Promise<void>;
  addStepCaptures: (steps: StepCaptureData[]) => Promise<void>;
  updateStepCapture: (stepNumber: number, files: File[]) => void;
  removeStepCapture: (stepNumber: number) => void;
  addStepDetails: (stepDetailsData: StepDetailsData) => Promise<void>;
  getStepDetails: (stepNumber: number) => Array<string> | undefined;
  getStepDetailsWithFields: (stepNumber: number) =>
    | Array<{
        label_id: string;
        label_title: string;
        label_type: string;
        value: string;
      }>
    | undefined;
  clearAllCaptures: (allowUploadedFiles?: boolean) => Promise<void>;
  getAllFiles: () => File[];
  getTotalSteps: () => number;
  getCompletedSteps: () => number;
  isStepCompleted: (stepNumber: number) => boolean;
  isRestoredFromDB: boolean;
}

export const MultiStepCaptureContext =
  createContext<MultiStepCaptureContextInterface>(
    {} as MultiStepCaptureContextInterface
  );

export const MultiStepCaptureProvider = ({ children }: PropsWithChildren) => {
  const [stepCaptures, setStepCaptures] = useState<StepCaptureData[]>([]);
  const [stepDetails, setStepDetails] = useState<StepDetailsData[]>([]);
  const [currentTaskId, setCurrentTaskIdState] = useState<string | null>(null);
  const [isRestoredFromDB, setIsRestoredFromDB] = useState<boolean>(false);
  const [isLoadingSteps, setIsLoadingSteps] = useState<boolean>(false);

  const setCurrentTaskId = useCallback(
    (taskId: string) => {
      if (taskId === currentTaskId) return;

      // Block step pages before the restore effect runs so an empty form can
      // never overwrite durable details during the first render for a task.
      setIsLoadingSteps(true);
      setIsRestoredFromDB(false);
      setCurrentTaskIdState(taskId);
    },
    [currentTaskId]
  );

  const invalidateCurrentTask = useCallback(() => {
    setStepCaptures([]);
    setStepDetails([]);
    setIsRestoredFromDB(false);
    setIsLoadingSteps(true);
    setCurrentTaskIdState(null);
  }, []);

  // Restore from IndexedDB when taskId is set
  useEffect(() => {
    if (!currentTaskId) return;

    let cancelled = false;

    const restoreFromDB = async () => {
      try {
        setIsLoadingSteps(true);
        setStepCaptures([]);
        setStepDetails([]);
        setIsRestoredFromDB(false);

        // Load step captures from IndexedDB (collection details handled separately)
        const hasStored = await captureDB.hasStoredProgress(currentTaskId);
        if (cancelled) return;

        if (hasStored) {
          const storedCaptures = await captureDB.getStepCaptures(currentTaskId);
          const storedDetails = await captureDB.getStepDetails(currentTaskId);
          if (cancelled) return;

          if (storedCaptures.length > 0 || storedDetails.length > 0) {
            // Keep step 0 available to Work Task pages that need the durable
            // task-wide collection details for QR codes and review.
            setStepCaptures(storedCaptures);
            setStepDetails(storedDetails);
            setIsRestoredFromDB(true);
          } else {
            setStepCaptures([]);
            setStepDetails([]);
            setIsRestoredFromDB(false);
          }
        } else {
          setStepCaptures([]);
          setStepDetails([]);
          setIsRestoredFromDB(false);
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to restore from IndexedDB:", error);
        setStepCaptures([]);
        setStepDetails([]);
        setIsRestoredFromDB(false);
      } finally {
        if (!cancelled) setIsLoadingSteps(false);
      }
    };

    restoreFromDB();

    return () => {
      cancelled = true;
    };
  }, [currentTaskId]);

  const addStepCapture = useCallback(
    async (stepData: StepCaptureData) => {
      if (!currentTaskId) {
        throw new Error("Cannot save step capture without a task ID");
      }

      const sessionMetadata =
        stepData.sessionMetadata ?? getActiveSessionMetadata();
      if (
        stepData.stepNumber > 0 &&
        (stepData.sessionMetadata !== undefined || loadSessionMetadata())
      )
        stepData = { ...stepData, sessionMetadata: { ...sessionMetadata } };
      await captureDB.saveStepCapture(currentTaskId, stepData);

      setStepCaptures((prev) => {
        const existingIndex = prev.findIndex(
          (s) => s.stepNumber === stepData.stepNumber
        );
        if (existingIndex >= 0) {
          // Update existing step
          const updated = [...prev];
          updated[existingIndex] = stepData;
          return updated;
        } else {
          // Add new step
          return [...prev, stepData].sort(
            (a, b) => a.stepNumber - b.stepNumber
          );
        }
      });
    },
    [currentTaskId]
  );

  const addStepCaptures = useCallback(
    async (steps: StepCaptureData[]) => {
      if (!currentTaskId)
        throw new Error("Cannot save captures without a task ID");
      const active = getActiveSessionMetadata();
      steps = steps.map((step) => {
        const sessionMetadata = step.sessionMetadata ?? active;
        return step.stepNumber > 0 &&
          (step.sessionMetadata !== undefined || loadSessionMetadata())
          ? { ...step, sessionMetadata: { ...sessionMetadata } }
          : step;
      });
      await captureDB.saveStepCaptures(currentTaskId, steps);
      setStepCaptures((previous) =>
        [
          ...previous.filter(
            (item) => !steps.some((step) => step.stepNumber === item.stepNumber)
          ),
          ...steps,
        ].sort((a, b) => a.stepNumber - b.stepNumber)
      );
    },
    [currentTaskId]
  );

  const updateStepCapture = useCallback(
    (stepNumber: number, files: File[]) => {
      setStepCaptures((prev) => {
        const updated = prev.map((step) =>
          step.stepNumber === stepNumber ? { ...step, files } : step
        );

        // Save updated step to IndexedDB
        if (currentTaskId) {
          const updatedStep = updated.find((s) => s.stepNumber === stepNumber);
          if (updatedStep) {
            captureDB
              .saveStepCapture(currentTaskId, updatedStep)
              .catch((error) => {
                console.error(
                  "Failed to update step capture in IndexedDB:",
                  error
                );
              });
          }
        }

        return updated;
      });
    },
    [currentTaskId]
  );

  const addStepDetails = useCallback(
    async (stepDetailsData: StepDetailsData) => {
      if (!currentTaskId) {
        throw new Error("Cannot save step details without a task ID");
      }

      await captureDB.saveStepDetails(currentTaskId, stepDetailsData);

      setStepDetails((prev) => {
        const existingIndex = prev.findIndex(
          (s) => s.stepNumber === stepDetailsData.stepNumber
        );
        if (existingIndex >= 0) {
          // Update existing step details
          const updated = [...prev];
          updated[existingIndex] = stepDetailsData;
          return updated;
        } else {
          // Add new step details
          return [...prev, stepDetailsData].sort(
            (a, b) => a.stepNumber - b.stepNumber
          );
        }
      });
    },
    [currentTaskId]
  );

  const getStepDetails = useCallback(
    (stepNumber: number) => {
      const stepDetailsData = stepDetails.find(
        (s) => s.stepNumber === stepNumber
      );
      return stepDetailsData?.stepDetails;
    },
    [stepDetails]
  );

  const getStepDetailsWithFields = useCallback(
    (stepNumber: number) => {
      const stepDetailsData = stepDetails.find(
        (s) => s.stepNumber === stepNumber
      );
      return stepDetailsData?.stepDetailsFields;
    },
    [stepDetails]
  );

  const removeStepCapture = useCallback(
    (stepNumber: number) => {
      setStepCaptures((prev) =>
        prev.filter((s) => s.stepNumber !== stepNumber)
      );
      setStepDetails((prev) => prev.filter((d) => d.stepNumber !== stepNumber));

      // Completely delete from IndexedDB
      if (currentTaskId) {
        captureDB.deleteStepData(currentTaskId, stepNumber).catch((error) => {
          console.error("Failed to delete step from IndexedDB:", error);
        });
      }
    },
    [currentTaskId]
  );

  const clearAllCaptures = useCallback(
    async (allowUploadedFiles = false) => {
      if (
        !allowUploadedFiles &&
        stepCaptures.some(
          (capture) =>
            capture.uploadCaptureId || capture.uploadedFileKeys?.length
        )
      ) {
        throw new Error(
          "Cannot delete progress after files have started uploading"
        );
      }

      // Clear durable progress before updating the UI so a failed deletion is
      // never presented as successful.
      if (currentTaskId) {
        await captureDB.clearTaskData(currentTaskId);
      }

      setStepCaptures([]);
      setStepDetails([]);
      setIsRestoredFromDB(false);
    },
    [currentTaskId, stepCaptures]
  );

  const getAllFiles = useCallback(() => {
    return stepCaptures.flatMap((step) => step.files);
  }, [stepCaptures]);

  const getTotalSteps = useCallback(() => {
    return stepCaptures.filter((step) => step.stepNumber > 0).length;
  }, [stepCaptures]);

  const getCompletedSteps = useCallback(() => {
    return stepCaptures.filter(
      (step) =>
        step.stepNumber > 0 && step.files.length >= step.numberOfCaptures
    ).length;
  }, [stepCaptures]);

  const isStepCompleted = useCallback(
    (stepNumber: number) => {
      const step = stepCaptures.find((s) => s.stepNumber === stepNumber);
      if (!step) return false;

      // Use the explicit isComplete flag if set, otherwise fall back to file count check
      if (step.isComplete !== undefined) {
        return step.isComplete;
      }

      // Fallback: check if step has required files (for backward compatibility)
      return step.files.length >= step.numberOfCaptures;
    },
    [stepCaptures]
  );

  return (
    <MultiStepCaptureContext.Provider
      value={{
        stepCaptures,
        stepDetails,
        currentTaskId,
        isLoadingSteps,
        setCurrentTaskId,
        invalidateCurrentTask,
        addStepCapture,
        addStepCaptures,
        updateStepCapture,
        removeStepCapture,
        addStepDetails,
        getStepDetails,
        getStepDetailsWithFields,
        clearAllCaptures,
        getAllFiles,
        getTotalSteps,
        getCompletedSteps,
        isStepCompleted,
        isRestoredFromDB,
      }}
    >
      {children}
    </MultiStepCaptureContext.Provider>
  );
};

export const useMultiStepCapture = () => {
  const context = useContext(MultiStepCaptureContext);
  if (!context) {
    throw new Error(
      "useMultiStepCapture must be used within a MultiStepCaptureProvider"
    );
  }
  return context;
};
